import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PRODUCTS = [
  {
    id: "metal-detox",
    name: "Metal Detox",
    tagline: "Zaštita boje, balayage-a i pramenova",
    description: "Neutralizira bakar unutar vlasi — manje pucanja, pouzdana i dugotrajna boja.",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/xcvssj2f_2d0c08df-3cce-42e1-a77f-ce82e2118a57.jpeg",
  },
  {
    id: "majirel",
    name: "Majirel",
    tagline: "Profesionalna boja za kosu",
    description: "True-to-tone boje s njegujućim sastojcima — pokrivanje sjedina i bogatstvo nijansi.",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/phcknj0n_309ef951-f2bf-449a-ac9f-5a0f499032f4.jpeg",
  },
  {
    id: "absolut-repair",
    name: "Absolut Repair",
    tagline: "Obnavljanje oštećene kose",
    description: "Šampon, maska, krema i ulje — kompletni tretman za revitalizaciju vlasi.",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/om4g0abi_17edb284-efd2-465b-8c42-092fa6e7e709.jpeg",
  },
  {
    id: "serie-expert",
    name: "Serie Expert",
    tagline: "Profesionalna njega za kosu",
    description: "Cijeli L'Oréal asortiman — od Blondifier i Vitamino do Pro Longer i Liss Unlimited.",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/oitnplmb_20d1d7a9-bd04-4969-b2f2-251db754625b.jpeg",
  },
  {
    id: "curl-expression",
    name: "Curl Expression",
    tagline: "Njega za kovrčavu kosu",
    description: "Pojačava, definira i hidratizira kovrče — bez crustinga i s prirodnim odskokom.",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/qxxfdvpd_39cbdb02-f4a2-4f8b-a7ec-428633380835.jpeg",
  },
];

export const ProductsCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setIndex((i) => (i + 1) % PRODUCTS.length), []);
  const prev = () => setIndex((i) => (i - 1 + PRODUCTS.length) % PRODUCTS.length);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next, paused]);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="products-carousel"
    >
      <div className="absolute -inset-4 border border-gold/20 -z-10 hidden md:block pointer-events-none" />
      <div className="relative aspect-square overflow-hidden bg-[#F5EFE3]">
        <div
          className="flex h-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ transform: `translateX(-${(index * 100) / PRODUCTS.length}%)`, width: `${PRODUCTS.length * 100}%` }}
        >
          {PRODUCTS.map((p) => (
            <div
              key={p.id}
              className="relative h-full shrink-0 bg-[#F5EFE3] flex items-center justify-center"
              style={{ width: `${100 / PRODUCTS.length}%` }}
              data-testid={`product-slide-${p.id}`}
            >
              <img
                src={p.image}
                alt={`L'Oréal ${p.name}`}
                className="w-full h-full object-contain"
                loading="eager"
              />
            </div>
          ))}
        </div>

        {/* Arrows */}
        <button
          type="button"
          onClick={prev}
          aria-label="Prethodni proizvod"
          data-testid="carousel-prev"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 border border-gold/50 bg-emerald-darker/70 backdrop-blur text-gold flex items-center justify-center hover:bg-gold hover:text-emerald-darker transition"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Sljedeći proizvod"
          data-testid="carousel-next"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 border border-gold/50 bg-emerald-darker/70 backdrop-blur text-gold flex items-center justify-center hover:bg-gold hover:text-emerald-darker transition"
        >
          <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-5 flex items-center justify-center gap-3">
        {PRODUCTS.map((p, i) => (
          <button
            type="button"
            key={p.id}
            onClick={() => setIndex(i)}
            aria-label={`Idi na ${p.name}`}
            data-testid={`carousel-dot-${i}`}
            className={`h-px transition-all duration-500 ${i === index ? "w-10 bg-gold" : "w-6 bg-gold/30 hover:bg-gold/60"}`}
          />
        ))}
      </div>

      {/* Counter + tagline */}
      <div className="mt-3 text-center">
        <p className="font-sans tracking-luxury text-[0.6rem] md:text-[0.65rem] uppercase text-gold/80 tabular-nums">
          {String(index + 1).padStart(2, "0")} / {String(PRODUCTS.length).padStart(2, "0")} · {PRODUCTS[index].name}
        </p>
        <p className="mt-1.5 font-serif italic text-sm md:text-base text-[#F5EFE3]/85">{PRODUCTS[index].tagline}</p>
      </div>
    </div>
  );
};
